import _styled from "styled-components";
const _excluded = ["className", "style", "children", "variant", "size", "disabled", "value", "onChange"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useControlled, variantSwitch, getValidChildren } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Button from '../Button';
const UNSET = 'unset';
const VARIANTS = {
  SECONDARY: Button.VARIANTS.SECONDARY,
  TERTIARY: Button.VARIANTS.TERTIARY
};
const SIZES = {
  SMALL: Button.SIZES.SMALL,
  MEDIUM: Button.SIZES.MEDIUM
};

/**
 * ButtonGroup used for grouping buttons
 * we want to group together and manage their selected state
 */
const ButtonGroup = _ref => {
  let {
      className,
      style,
      children,
      variant = VARIANTS.SECONDARY,
      size = SIZES.MEDIUM,
      disabled,
      value,
      onChange
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [selected, setSelected] = useControlled(UNSET, value, onChange);

  // if has only one button return button instead of ButtonGroup
  if (React.Children.count(children) === 1) {
    return children;
  }
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    className: className,
    "aria-selected": !!selected && selected !== UNSET
  }, props, {
    $_css: [{
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.borderRadius.small
    }, variantSwitch({
      statement: variant,
      variants: {
        [VARIANTS.SECONDARY]: {
          transition: theme.transitions.create(['box-shadow', 'border']),
          '&:hover': {
            boxShadow: theme.halos.hover
          },
          '& > button:first-child': {
            borderRadius: "".concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.none, " ").concat(theme.borderRadius.none, " ").concat(theme.borderRadius.small, "px")
          },
          '& > button:not(:first-child):not(:last-child)': {
            borderRadius: theme.borderRadius.none
          },
          '& > button:last-child': {
            borderRadius: "".concat(theme.borderRadius.none, " ").concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.none)
          }
        },
        [VARIANTS.TERTIARY]: _objectSpread({
          '& > button:first-child': {
            borderRadius: "".concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.none, " ").concat(theme.borderRadius.none, " ").concat(theme.borderRadius.small, "px"),
            borderRight: "1px solid ".concat(theme.tokens.colors.border.subtle.value)
          },
          '& > button:not(:first-child):not(:last-child)': {
            borderRadius: theme.borderRadius.none,
            borderRight: "1px solid ".concat(theme.tokens.colors.border.subtle.value)
          },
          '& > button:last-child': {
            borderRadius: "".concat(theme.borderRadius.none, " ").concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.small, "px ").concat(theme.borderRadius.none)
          },
          '& > button': {
            zIndex: 1
          },
          '&:hover': {
            button: {
              borderRightColor: theme.tokens.colors.border.inverse.value
            },
            '& > button:not(:first-child):not(:last-child)': {
              borderRightColor: theme.tokens.colors.border.inverse.value
            }
          }
        }, selected !== UNSET && {
          button: {
            borderRightColor: theme.tokens.colors.border.inverse.value
          }
        })
      }
    }), style]
  }), React.Children.map(getValidChildren(children), (child, position) => {
    const selectionValue = child.props.value || position;
    return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
      selected: selected === selectionValue,
      onClick: e => {
        if (child.props.onClick) {
          child.props.onClick(e);
        }
        setSelected(selectionValue);
      },
      variant,
      size,
      disabled: disabled || child.props.disabled,
      style: [{
        '&:hover': _objectSpread({
          boxShadow: 'none'
        }, selected !== selectionValue && {
          background: theme.tokens.colors.background.neutral.hover.value
        }),
        '&:focus': {
          boxShadow: theme.halos.focus,
          zIndex: 10
        },
        '&:active': {
          boxShadow: theme.halos.active
        },
        '&[disabled]': {
          '&:hover': {
            background: theme.tokens.colors.background.primary.value
          }
        }
      }, variantSwitch({
        statement: variant,
        variants: {
          [VARIANTS.SECONDARY]: {
            '&[disabled]': {
              borderColor: theme.tokens.colors.border.value,
              color: theme.tokens.colors.text.inactive.value,
              svg: {
                color: theme.tokens.colors.icon.inactive.value
              }
            }
          },
          [VARIANTS.TERTIARY]: {
            '&[disabled]': {
              color: theme.tokens.colors.text.inactive.value,
              svg: {
                color: theme.tokens.colors.icon.inactive.value
              }
            }
          }
        }
      }), child.props.style]
    }));
  }));
};
ButtonGroup.VARIANTS = VARIANTS;
ButtonGroup.SIZES = SIZES;
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  /**  The component's children tabs */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The style variant of buttons, defaults to 'secondary' */
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  /**  The size of buttons, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(SIZES)),
  /** Disable buttons functionality */
  disabled: PropTypes.bool,
  /** value for controlled usage */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** onChange handler for controlled usage */
  onChange: PropTypes.func
};
export default ButtonGroup;
var _StyledDiv = _styled("div").withConfig({
  displayName: "ButtonGroup___StyledDiv",
  componentId: "sc-z8cvw6-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=ButtonGroup.js.map